/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {Inject, Injectable, Optional} from "@angular/core";
import {CommonActions} from "@app/store/common/common.actions";
import {CommonState} from "@app/store/common/common.state";
import {Store} from "@ngxs/store";
import {AuthInfo} from "@trec/api/common";
import {SessionManagerService} from "@trec/api/session-manager";
import {TREC_TOKEN_RENEW_INTERVAL, TrecTokenRef} from "@trec/api/token-ref";
import {TrecAuthStoreService} from "@trec/api/utils";
import {Observable} from "rxjs";
import {skipWhile} from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class StoreTrecTokenRef extends TrecTokenRef {
  constructor(
    private store: Store,
    private tokenService: TrecAuthStoreService,
    sessionManager: SessionManagerService,
    @Optional() @Inject(TREC_TOKEN_RENEW_INTERVAL) tokenRenewInterval: number | null
  ) {
    super();
  }

  getAuthInfo(): Observable<AuthInfo | null> {
    // Return the token but skip until token has been not set
    return this.store.select(CommonState.authInfo).pipe(skipWhile((a) => a?.token === "notset"));
  }

  getAuthInfoSnapshot(): AuthInfo | null {
    return this.store.selectSnapshot(CommonState.authInfo);
  }

  updateAuthInfo(authInfo: AuthInfo | null): Observable<AuthInfo | null> {
    const oldToken = this.store.selectSnapshot(CommonState.trecToken);

    if (authInfo?.token !== oldToken) {
      // Save new token in local storage
      this.tokenService.saveAuthInfo(authInfo);
      // Save new token in store
      this.store.dispatch(
        new CommonActions.SetToken({
          token: authInfo?.token ?? null,
          trecUserId: authInfo?.trecUserId ?? null,
        })
      );
      // Signal new token
      this.onNewToken(authInfo);
    }

    return this.getAuthInfo();
  }
}
